import React, { useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyEuroIcon,
  Square2StackIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';

import type { Criterion, CriterionType, CriterionScope } from '../types';
import Switch from './Switch';

interface Props {
  open: boolean;
  criteria: Criterion[];
  validated: boolean;
  onClose: () => void;
  onCreateCriterion: (c: Omit<Criterion, 'id' | 'order'>) => void;
  onUpdateCriterion: (id: string, patch: Partial<Criterion>) => void;
  onDeleteCriterion: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
  onReextract: () => void;
  onValidate: () => void;
}

/**
 * Overlay (modale pleine fenêtre) d'édition + validation des critères.
 *
 * S'ouvre automatiquement après l'extraction IA depuis le RC (signalée par le
 * workspace via le bandeau "Critères à relire"). L'analyste peut :
 *  - relire, réorganiser, éditer, pondérer, activer/désactiver
 *  - ajouter un critère manquant ou ré-extraire
 *  - cliquer "Valider les critères" → bascule `project.criteriaValidated = true`
 *    et permet de noter dans le workspace.
 */
const CriteriaOverlay: React.FC<Props> = ({
  open,
  criteria,
  validated,
  onClose,
  onCreateCriterion,
  onUpdateCriterion,
  onDeleteCriterion,
  onToggleActive,
  onReextract,
  onValidate,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<
    | { mode: 'edit'; criterion: Criterion }
    | { mode: 'new'; parentId: string | null }
    | null
  >(null);
  const [confirmReextract, setConfirmReextract] = useState(false);

  // À l'ouverture, déplier les racines pour une lecture rapide
  useEffect(() => {
    if (!open) return;
    setExpanded(new Set(criteria.filter(c => c.parentId === null).map(c => c.id)));
  }, [open, criteria]);

  // ESC ferme
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const tree = useMemo(() => buildTree(criteria), [criteria]);
  const leafIds = useMemo(() => {
    const parents = new Set(criteria.map(c => c.parentId).filter(Boolean) as string[]);
    return new Set(criteria.filter(c => !parents.has(c.id)).map(c => c.id));
  }, [criteria]);
  const activeLeaves = criteria.filter(c => leafIds.has(c.id) && c.active).length;
  const totalPoints = criteria
    .filter(c => leafIds.has(c.id) && c.active)
    .reduce((acc, c) => acc + c.weight, 0);

  const toggleExpand = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(criteria.map(c => c.id)));
  const collapseAll = () => setExpanded(new Set());

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="criteria-overlay-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <header className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2
                id="criteria-overlay-title"
                className="text-lg font-semibold text-gray-900"
              >
                Critères d'évaluation
              </h2>
              {!validated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                  À valider
                </span>
              )}
              {validated && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium">
                  <CheckBadgeIcon className="w-3.5 h-3.5" />
                  Validés
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-600 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs font-medium tabular-nums">
                {activeLeaves} actifs
              </span>
              <span className="text-gray-400">·</span>
              <span className="tabular-nums text-xs">
                {criteria.length} critères au total
              </span>
              <span className="text-gray-400">·</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700 text-white text-xs font-semibold tabular-nums">
                {totalPoints} pts
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </header>

        {/* Toolbar */}
        <div className="shrink-0 flex items-center justify-between px-6 py-2.5 border-b border-gray-100 text-xs">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={expandAll}
              className="text-gray-600 hover:text-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded"
            >
              Tout déplier
            </button>
            <span className="text-gray-300">·</span>
            <button
              type="button"
              onClick={collapseAll}
              className="text-gray-600 hover:text-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded"
            >
              Tout replier
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditTarget({ mode: 'new', parentId: null })}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Nouveau critère
            </button>
            <button
              type="button"
              onClick={() => setConfirmReextract(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <SparklesIcon className="w-3.5 h-3.5" />
              Ré-extraire
            </button>
          </div>
        </div>

        {/* Corps scrollable : arbre */}
        <div className="flex-1 overflow-y-auto">
          {criteria.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-50 text-violet-700 mb-4">
                <SparklesIcon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Aucun critère pour le moment
              </h3>
              <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
                Déposez d'abord un règlement de consultation — l'IA extraira automatiquement les critères.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {tree.map(node => (
                <CriterionNode
                  key={node.id}
                  node={node}
                  level={0}
                  expanded={expanded}
                  onToggle={toggleExpand}
                  onEdit={c => setEditTarget({ mode: 'edit', criterion: c })}
                  onAddChild={parentId => setEditTarget({ mode: 'new', parentId })}
                  onDelete={onDeleteCriterion}
                  onToggleActive={onToggleActive}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Footer sticky : validation */}
        <footer className="shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-600">
            {validated ? (
              <span>
                Les critères sont validés. La notation est activée dans l'espace de travail.
              </span>
            ) : (
              <span>
                Tant que les critères ne sont pas validés, la notation reste désactivée dans l'espace de travail.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              {validated ? 'Fermer' : 'Annuler'}
            </button>
            {!validated && (
              <button
                type="button"
                onClick={onValidate}
                disabled={activeLeaves === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
              >
                <CheckBadgeIcon className="w-4 h-4" />
                Valider les critères
              </button>
            )}
          </div>
        </footer>
      </div>

      {/* Sous-modales : édition critère + confirm ré-extraction */}
      {editTarget && (
        <CriterionEditModal
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={data => {
            if (editTarget.mode === 'edit') {
              onUpdateCriterion(editTarget.criterion.id, data);
            } else {
              onCreateCriterion({
                parentId: editTarget.parentId,
                code: data.code ?? '',
                title: data.title ?? '',
                description: data.description,
                weight: data.weight ?? 0,
                type: data.type ?? 'non_financial',
                scope: data.scope ?? 'global',
                active: data.active ?? true,
              });
            }
            setEditTarget(null);
          }}
        />
      )}

      {confirmReextract && (
        <ConfirmReextractModal
          onCancel={() => setConfirmReextract(false)}
          onConfirm={() => {
            onReextract();
            setConfirmReextract(false);
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Arbre
// ─────────────────────────────────────────────────────────────────

interface TreeNode extends Criterion {
  children: TreeNode[];
}

function buildTree(criteria: Criterion[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  criteria.forEach(c => byId.set(c.id, { ...c, children: [] }));
  const roots: TreeNode[] = [];
  for (const c of criteria) {
    const node = byId.get(c.id)!;
    if (c.parentId && byId.has(c.parentId)) {
      byId.get(c.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortFn = (a: TreeNode, b: TreeNode) => a.order - b.order;
  roots.sort(sortFn);
  byId.forEach(n => n.children.sort(sortFn));
  return roots;
}

interface CriterionNodeProps {
  node: TreeNode;
  level: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onEdit: (c: Criterion) => void;
  onAddChild: (parentId: string) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, active: boolean) => void;
}

const CriterionNode: React.FC<CriterionNodeProps> = ({
  node,
  level,
  expanded,
  onToggle,
  onEdit,
  onAddChild,
  onDelete,
  onToggleActive,
}) => {
  const isOpen = expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const dimmed = !node.active;

  return (
    <li>
      <div
        className={`flex items-center gap-2 px-6 py-2.5 transition-colors ${
          dimmed ? 'bg-gray-50' : 'hover:bg-gray-50'
        }`}
        style={{ paddingLeft: `${24 + level * 24}px` }}
      >
        <button
          type="button"
          onClick={() => onToggle(node.id)}
          className={`shrink-0 p-0.5 rounded text-gray-400 ${
            hasChildren ? 'hover:text-gray-700' : 'invisible'
          } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1`}
          aria-label={isOpen ? 'Replier' : 'Déplier'}
          disabled={!hasChildren}
        >
          {isOpen ? (
            <ChevronDownIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
        </button>

        <span className="shrink-0 inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs font-semibold tabular-nums">
          {node.code}
        </span>

        <div className="flex-1 min-w-0">
          <div
            className={`text-sm leading-snug ${
              dimmed ? 'text-gray-500 line-through decoration-gray-400' : 'text-gray-900'
            } ${level === 0 ? 'font-semibold' : 'font-medium'}`}
          >
            {node.title}
          </div>
          {node.description && isOpen && (
            <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{node.description}</div>
          )}
        </div>

        <div className="hidden md:flex items-center gap-1.5 shrink-0">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${
              node.type === 'financial'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-gray-100 text-gray-700 border-gray-200'
            }`}
            title={node.type === 'financial' ? 'Critère financier' : 'Critère non financier'}
          >
            <CurrencyEuroIcon className="w-3 h-3" />
            {node.type === 'financial' ? 'Financier' : 'Non fin.'}
          </span>
          {node.scope === 'lot' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-gray-100 text-gray-700 border-gray-200 text-xs font-medium">
              <Square2StackIcon className="w-3 h-3" />
              Par lot
            </span>
          )}
        </div>

        <span
          className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold tabular-nums ${
            dimmed ? 'bg-gray-200 text-gray-500' : 'bg-gray-700 text-white'
          }`}
        >
          {node.weight} pts
        </span>

        <div className="shrink-0 flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onEdit(node)}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
            aria-label={`Modifier ${node.code} ${node.title}`}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onAddChild(node.id)}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
            aria-label={`Ajouter un sous-critère à ${node.code}`}
          >
            <PlusIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(node.id)}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
            aria-label={`Supprimer ${node.code}`}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          <div className="pl-2 border-l border-gray-200 ml-1">
            <Switch
              checked={node.active}
              onChange={v => onToggleActive(node.id, v)}
              label={`${node.active ? 'Désactiver' : 'Activer'} ${node.code}`}
            />
          </div>
        </div>
      </div>

      {isOpen && hasChildren && (
        <ul>
          {node.children.map(child => (
            <CriterionNode
              key={child.id}
              node={child}
              level={level + 1}
              expanded={expanded}
              onToggle={onToggle}
              onEdit={onEdit}
              onAddChild={onAddChild}
              onDelete={onDelete}
              onToggleActive={onToggleActive}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// ─────────────────────────────────────────────────────────────────
// Modale d'édition critère
// ─────────────────────────────────────────────────────────────────

type EditModalTarget =
  | { mode: 'edit'; criterion: Criterion }
  | { mode: 'new'; parentId: string | null };

interface CriterionEditModalProps {
  target: EditModalTarget;
  onClose: () => void;
  onSave: (data: Partial<Criterion>) => void;
}

const CriterionEditModal: React.FC<CriterionEditModalProps> = ({ target, onClose, onSave }) => {
  const existing = target.mode === 'edit' ? target.criterion : undefined;
  const [code, setCode] = useState(existing?.code ?? '');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [weight, setWeight] = useState<number>(existing?.weight ?? 0);
  const [type, setType] = useState<CriterionType>(existing?.type ?? 'non_financial');
  const [scope, setScope] = useState<CriterionScope>(existing?.scope ?? 'global');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      code: code.trim(),
      title: title.trim(),
      description: description.trim() || undefined,
      weight,
      type,
      scope,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-criterion-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="edit-criterion-title" className="text-lg font-semibold text-gray-900">
            {target.mode === 'edit' ? 'Modifier le critère' : 'Nouveau critère'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                placeholder="1.1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Intitulé</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ex : Performance énergétique de la solution"
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Guide d'évaluation
              <span className="ml-1.5 font-normal text-gray-400">(optionnel)</span>
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="Décrivez ce qui est attendu pour atteindre 100%, 50%, 0%…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pondération</label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  value={weight}
                  onChange={e => setWeight(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm text-gray-900 tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                  pts
                </span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <div className="grid grid-cols-2 gap-1 p-1 border border-gray-300 rounded-lg">
                {(['non_financial', 'financial'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                      type === t
                        ? 'bg-violet-50 text-violet-700 border border-violet-200'
                        : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    {t === 'financial' ? 'Financier' : 'Non financier'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Périmètre</label>
            <div className="grid grid-cols-2 gap-1 p-1 border border-gray-300 rounded-lg w-fit">
              {(['global', 'lot'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                    scope === s
                      ? 'bg-violet-50 text-violet-700 border border-violet-200'
                      : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  {s === 'global' ? 'Global (tous lots)' : 'Par lot'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!title.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Confirm ré-extraction
// ─────────────────────────────────────────────────────────────────

interface ConfirmReextractModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmReextractModal: React.FC<ConfirmReextractModalProps> = ({ onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
    onClick={onCancel}
  >
    <div
      className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 p-2 rounded-lg bg-amber-50 text-amber-700">
          <ExclamationTriangleIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Ré-extraire les critères ?</h2>
          <p className="text-sm text-gray-600">
            Cette opération écrase les critères actuels (y compris vos modifications manuelles)
            et relance l'extraction IA depuis le règlement.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
        >
          <SparklesIcon className="w-4 h-4" />
          Ré-extraire
        </button>
      </div>
    </div>
  </div>
);

export default CriteriaOverlay;
